import { useEffect, useRef, useState } from "react";
import { useAppSelector } from "./redux";
import type { IAnalyticsController } from "../hexagon/adapters/driver/analyticsController";
import { isMobile } from "../util/layout";
import {
  shouldExcludePath,
  markManuallyOpenedThisSession,
  type IntercomTriggerOptions,
} from "../util/intercomTrigger";
import { isCompassusExperience } from "../util/isCompassusUser";

interface IUseIntercom {
  webIntercomProvider: IWebIntercomProvider;
  analyticsController: IAnalyticsController;
}

export const useIntercom = ({
  webIntercomProvider,
  analyticsController,
}: IUseIntercom) => {
  const [toggle, setToggle] = useState(false);
  const hasBootedRef = useRef(false);

  const { user, userClinic } = useAppSelector((state) => ({
    user: state.auth.user,
    userClinic: state.clinic.userClinic,
  }));
  const isHomeHealthExternal = isCompassusExperience({ user, userClinic });

  useEffect(() => {
    if (!user || hasBootedRef.current) return;

    const bootPayload = {
      hide_default_launcher: isHomeHealthExternal,
      name: user?.fullName || undefined,
      facility: userClinic?.name || undefined,
      email: user?.email || undefined,
      user_id: user?.userID || undefined,
      is_compassus: isHomeHealthExternal,
    };

    webIntercomProvider.boot(bootPayload);
    if (isHomeHealthExternal) {
      webIntercomProvider.hide();
    }
    hasBootedRef.current = true;
  }, [isHomeHealthExternal, user, userClinic, webIntercomProvider]);

  useEffect(() => {
    // Global event listener for intercom triggers
    const handleIntercomEvent = (event: Event) => {
      // Compassus users open Intercom from Settings only.
      if (isHomeHealthExternal) return;

      // Don't trigger on mobile
      if (isMobile()) return;

      const customEvent = event as CustomEvent<IntercomTriggerOptions>;
      const options = customEvent.detail || {};
      const { delay = 0, excludePaths = [] } = options;

      // Check if current path should be excluded
      if (shouldExcludePath(excludePaths)) {
        return;
      }

      if (delay > 0) {
        setTimeout(() => {
          handleToggle();
        }, delay);
      } else {
        handleToggle();
      }
    };

    // Add global event listener
    window.addEventListener("openIntercom", handleIntercomEvent);

    return () => {
      webIntercomProvider.shutdown();
      window.removeEventListener("openIntercom", handleIntercomEvent);
    };
  }, [isHomeHealthExternal, webIntercomProvider]);

  useEffect(() => {
    const updatePayload = {
      hide_default_launcher: isHomeHealthExternal,
      name: user?.fullName || undefined,
      facility: userClinic?.name || undefined,
      email: user?.email || undefined,
      user_id: user?.userID || undefined,
      is_compassus: isHomeHealthExternal,
    };

    if (!user) {
      return;
    }

    webIntercomProvider.update(updatePayload);
    if (isHomeHealthExternal) {
      webIntercomProvider.hide();
    }
  }, [isHomeHealthExternal, user, userClinic, webIntercomProvider]);

  const tapEvent = () => {
    analyticsController.logEvent("Tap on intercom icon", {
      email: user?.email,
      selectedFacility: userClinic?.name,
      selectedFacilityId: userClinic?.id,
      cUserId: user?.userID,
    });
  };

  const handleWebToggle = () => {
    if (toggle === true) {
      webIntercomProvider.hide();
      setToggle(false);
    } else {
      tapEvent();
      setToggle(true);

      // Mark that user manually opened Intercom this session
      // This prevents auto-trigger for the remainder of this session
      markManuallyOpenedThisSession();

      webIntercomProvider.show();
    }
  };

  // const handleNativeToggle = async () => {
  //   if (toggle === true) {
  //     await circadiaEngineProvider.hideIntercom();
  //     setToggle(false);
  //   } else {
  //     await circadiaEngineProvider.showIntercom();
  //     tapEvent();
  //     setToggle(true);
  //   }
  // };

  const handleToggle = async () => {
    // Compassus users open Intercom from the Settings support entry only.
    if (isHomeHealthExternal) return;

    // Temporary disable native intercom
    // if (false) {
    //   handleNativeToggle();
    //   return;
    // }
    handleWebToggle();
  };

  webIntercomProvider.onHide(() => {
    setToggle(false);
  });

  webIntercomProvider.onShow(() => {
    setToggle(true);
  });

  return { handleToggle };
};
